import {
    ExtendedMonitoredTv,
    MilitaryZoneDto,
    MonitoredMeasure,
    NetworkMapConvection,
    NetworkMeteoForecast,
    NiaMeasureImpact,
    PolygonMeteoForecast,
    MonitoredRegulationOutlook,
    TrafficVolumeMeteoForecast,
    TvImpact,
    TvImpactTypeEnum,
} from 'app/model/SOL49';
import { ProvidesKey } from 'app/model/client/CORE';
import {
    Airspace,
    HotspotDto,
    MapTrafficVolume,
    MCDM_ONLY_DTO_CLAZZ_VALUE,
    OTSRoute,
    REGULATION_DTO_CLAZZ_VALUE,
    REGULATION_OR_MCDM_ONLY_DTO_CLAZZ_VALUE,
    REGULATION_PROPOSAL_DTO_CLAZZ_VALUE,
    RegulationOrMcdmOnlyDto,
    TimePeriod,
} from 'app/model/CORE';
import Utils, { Comparator } from 'app/common/util/Utils';
import memoizeOne from 'memoize-one';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import { MapConvection } from 'app/components/map/CoreMap';
import { ReactComponentElement } from 'react';
import TimeUtils from 'app/common/util/TimeUtils';
import { AlertType } from 'app/components/map/util/MapUtils';

export enum MapLayerType {
    TV,
    MEASURE,
    CUSTOM,
    WEATHER,
    ENV,
    WORKSPACE,
    HOTSPOT,
}

export enum MapObjectType {
    TV,
    MEASURE,
    REGULATION_OUTLOOK,
    HOTSPOT,
    CONVECTION,
    ACC,
    MILITARY,
    NAT_TRACK,
    IMPACTED_TV,
    IMPACTED_MEASURE,
    SCENARIOS,
}

export type MapLayerFilterFunction = (value: ObjectToDisplay, filter?: MapLayerFilter) => boolean;

export interface MapLayerFilter {
    name: string;
    active: boolean;
    filterKeys: MapLayerFilterFunction | Array<string>;
    id: number;
}

export type ObjectToDisplay = { type: MapObjectType; object: any };

export class WeatherMinLevel {
    private level: number;

    constructor(level: number) {
        this.level = level;
    }

    getLevel() {
        return this.level;
    }
}

export type MapLayerCustomProperties = {
    [key: string]:
        | string
        | boolean
        | number
        | WeatherMinLevel
        | RegulatedLayerDelayLevelEnum
        | RegulationOutlookPredictionEnum
        | AlertType;
};
export type MapLayerParameters = { [key: string]: any };

export interface MapLayer {
    id: number;
    objectsToDisplay: Array<ObjectToDisplay>;
    type: MapLayerType; //can be removed?
    mapType: Array<MapObjectType>; //not quite sure if it will be required
    active?: boolean;
    showLabel?: boolean;
    filters?: Array<MapLayerFilter>;
    priority: number; //lower the value lower the priority (lower values will be added to the map first and so the others would be displayed on top)
    group?: string;
    selection: Array<ObjectToDisplay>;
    label: string;
    customProperties?: MapLayerCustomProperties; //properties that can be changed by the user in the hmi
    parameters?: MapLayerParameters; //generic usage, not to be changed in the hmi
    customActions?: { [key: string]: MapLayerCustomAction }; //label -> action
}

export interface MapLayerCustomAction {
    icon?: ReactComponentElement<any>;
    handler(mapLayer: MapLayer): void;
}

export class MapLayerUtils {
    static TV_MAP_SELECTION_POPUP_FIELD: string = 'trafficVolumes';
    static MEASURE_MAP_SELECTION_POPUP_FIELD: string = 'measures';
    static HOTSPOT_MAP_SELECTION_POPUP_FIELD: string = 'hotspots';
    static REG_OUTLOOK_MAP_SELECTION_POPUP_FIELD: string = 'regOutlook';
    static CONVECTION_MAP_SELECTION_POPUP_FIELD: string = 'convection';
    static TV_LAYER_COUNT_TYPE_PROPERTY: string = 'countType';
    static TV_LAYER_LOAD_PROPERTY: string = 'Load to show';
    static TV_LAYER_SHOW_NON_MONITORABLE_PROPERTY: string = 'Show non-monitorable';
    static TV_LAYER_SHOW_LEGEND_PROPERTY: string = 'Legend';
    static TV_LAYER_FILTERED_TV_IDS_PARAMETER: string = 'FilteredTvIds';
    static LAYER_ACTIVE_CHANGE_DISABLE: string = 'LayerActiveChangeDisable'; //if true the user can't change the layer active value

    static EXTENDED_MONITORED_TV_KEY_PROVIDER: ProvidesKey<ExtendedMonitoredTv> = new ProvidesKey<ExtendedMonitoredTv>(
        item => item.refLocation.id,
    );
    static EXTENDED_MONITORED_TV_TV_ID_PROVIDER: ProvidesKey<ExtendedMonitoredTv> =
        new ProvidesKey<ExtendedMonitoredTv>(item => item.refLocation.id);
    static IMPACTED_TV_TV_ID_PROVIDER: ProvidesKey<TvImpact> = new ProvidesKey<TvImpact>(item => item.tvId);
    static IMPACTED_MEASURE_TV_ID_PROVIDER: ProvidesKey<NiaMeasureImpact> = new ProvidesKey<NiaMeasureImpact>(
        item => item.tvId,
    );
    static IMPACTED_TV_KEY_PROVIDER: ProvidesKey<TvImpact> = new ProvidesKey<TvImpact>(item => item.tvId);
    static IMPACTED_MEASURE_KEY_PROVIDER: ProvidesKey<NiaMeasureImpact> = new ProvidesKey<NiaMeasureImpact>(
        item => item.regulId,
    );
    static MONITORED_MEASURE_KEY_PROVIDER: ProvidesKey<MonitoredMeasure> = new ProvidesKey<MonitoredMeasure>(
        item => item.measure.measureId.id,
    );
    static MONITORED_MEASURE_TV_ID_PROVIDER: ProvidesKey<MonitoredMeasure> = new ProvidesKey<MonitoredMeasure>(item =>
        MapLayerUtils.isValidMapMeasure(item) ? (item.measure as RegulationOrMcdmOnlyDto).location.id : null,
    );
    static HOTSPOT_TV_ID_PROVIDER: ProvidesKey<HotspotDto> = new ProvidesKey<HotspotDto>(item => item.hotspotId.tvId);
    static MONITORED_REG_OUTLOOK_ID_PROVIDER: ProvidesKey<MonitoredRegulationOutlook> =
        new ProvidesKey<MonitoredRegulationOutlook>(item => item.regulationOutlook.aerodromeId);
    static NAT_TRACK_KEY_PROVIDER: ProvidesKey<OTSRoute> = new ProvidesKey<OTSRoute>(item => item.id); //todo: include availability period in the key?

    static VALID_MEASURE_CLASSES = [
        REGULATION_OR_MCDM_ONLY_DTO_CLAZZ_VALUE,
        MCDM_ONLY_DTO_CLAZZ_VALUE,
        REGULATION_DTO_CLAZZ_VALUE,
        REGULATION_PROPOSAL_DTO_CLAZZ_VALUE,
    ];

    static MILITARY_ZONES_CACHE: Array<MilitaryZoneDto> = []; //so it can be used outside the fullscreen data

    static getObjectToDisplayKey(objectToDisplay: ObjectToDisplay) {
        switch (objectToDisplay.type) {
            case MapObjectType.TV:
                return this.EXTENDED_MONITORED_TV_KEY_PROVIDER.getKey(objectToDisplay.object);
            case MapObjectType.MEASURE:
                return this.MONITORED_MEASURE_KEY_PROVIDER.getKey(objectToDisplay.object);
            case MapObjectType.HOTSPOT:
                return this.HOTSPOT_TV_ID_PROVIDER.getKey(objectToDisplay.object);
            case MapObjectType.NAT_TRACK:
                return this.NAT_TRACK_KEY_PROVIDER.getKey(objectToDisplay.object);
            case MapObjectType.IMPACTED_TV:
                return this.IMPACTED_TV_KEY_PROVIDER.getKey(objectToDisplay.object);
            case MapObjectType.IMPACTED_MEASURE:
                return this.IMPACTED_MEASURE_KEY_PROVIDER.getKey(objectToDisplay.object);
            case MapObjectType.CONVECTION:
            case MapObjectType.ACC:
            case MapObjectType.MILITARY:
                return null; //TODO those are not eligeble to be added to custom layer, so no problem returning null
        }
    }

    static toObjectToDisplayArray(objects: Array<any>, type: MapObjectType): Array<ObjectToDisplay> {
        if (objects) {
            return objects?.map(obj => ({
                type: type,
                object: obj,
            }));
        } else {
            return [];
        }
    }

    static isValidMapMeasure(measure: MonitoredMeasure): boolean {
        return Utils.contains(MapLayerUtils.VALID_MEASURE_CLASSES, measure.measure.clazz);
    }

    static getLayerObjectsToDisplayTvIds(layer: MapLayer): Array<string> {
        return this.getObjectsToDisplayTvIds(layer.objectsToDisplay);
    }

    static getObjectsToDisplayTvIds(objectsToDisplay: Array<ObjectToDisplay>) {
        if (objectsToDisplay)
            return objectsToDisplay
                .map((objectToDisplay: ObjectToDisplay) => this.getObjectToDisplayTvId(objectToDisplay))
                ?.filter(tvId => Utils.isExists(tvId));
        return [];
    }

    static getObjectToDisplayTvId(object: ObjectToDisplay) {
        switch (object.type) {
            case MapObjectType.TV:
                return this.EXTENDED_MONITORED_TV_TV_ID_PROVIDER.getKey(object.object);
            case MapObjectType.MEASURE:
                return this.MONITORED_MEASURE_TV_ID_PROVIDER.getKey(object.object);
            case MapObjectType.REGULATION_OUTLOOK:
                return undefined; // RegOutlook object contains the aerodrome point to be displayed in the DTO
            case MapObjectType.HOTSPOT:
                return this.HOTSPOT_TV_ID_PROVIDER.getKey(object.object);
            case MapObjectType.IMPACTED_TV:
                return this.IMPACTED_TV_TV_ID_PROVIDER.getKey(object.object);
            case MapObjectType.IMPACTED_MEASURE:
                return this.IMPACTED_MEASURE_TV_ID_PROVIDER.getKey(object.object);
            case MapObjectType.CONVECTION:
                return null;
            case MapObjectType.ACC:
            case MapObjectType.MILITARY:
                return null; //TODO
        }
    }

    static hasCustomProperty(mapLayer: MapLayer, key: string): boolean {
        return Utils.isExists(mapLayer.customProperties) && Utils.isExists(mapLayer.customProperties[key]);
    }

    static hasObjectToDisplay(mapLayer: MapLayer, objectToDisplay: ObjectToDisplay): boolean {
        if (mapLayer?.objectsToDisplay?.length) {
            return Utils.isExists(
                mapLayer.objectsToDisplay.find(
                    otd =>
                        otd.type === objectToDisplay.type &&
                        this.getObjectToDisplayKey(otd) === this.getObjectToDisplayKey(objectToDisplay),
                ),
            );
        }
        return false;
    }

    static getMapObjectTypeName(type: MapObjectType): string {
        switch (type) {
            case MapObjectType.TV:
                return 'TV';
            case MapObjectType.MEASURE:
                return 'Measure';
            case MapObjectType.HOTSPOT:
                return 'Hotspot';
            case MapObjectType.CONVECTION:
                return 'Convection';
            case MapObjectType.ACC:
                return 'ACC';
            case MapObjectType.MILITARY:
                return 'Military';
            case MapObjectType.NAT_TRACK:
                return 'NAT Track';
        }
    }

    static hasStatusChangeEnabled(mapLayer: MapLayer): boolean {
        if (
            mapLayer.parameters &&
            Utils.isExists(mapLayer.parameters[this.LAYER_ACTIVE_CHANGE_DISABLE]) &&
            mapLayer.parameters[this.LAYER_ACTIVE_CHANGE_DISABLE] === true
        ) {
            return false;
        }
        return true;
    }
}

const CUSTOM_LAYERS_LS_KEY = 'customLayers';

export const MapObjectToDisplayComparator: Comparator = (a1, a2): boolean => {
    return a1.type === a2.type && MapLayerUtils.getObjectToDisplayKey(a1) === MapLayerUtils.getObjectToDisplayKey(a2);
};

export class CustomLayerUtils {
    static GROUP_LAYER = 'Custom layers';

    static customLayers: Array<MapLayer> = CustomLayerUtils.loadFromStorage();

    static updateStorage(values: Array<MapLayer>) {
        const newValue = values.filter(lts => lts.type === MapLayerType.CUSTOM);
        localStorage.setItem(CUSTOM_LAYERS_LS_KEY, JSON.stringify(newValue)); //TODO minify objects to display and selection and stringify dates correctly
        CustomLayerUtils.customLayers = newValue;
        return newValue;
    }

    static loadFromStorage(): Array<MapLayer> {
        if (localStorage.getItem(CUSTOM_LAYERS_LS_KEY)) {
            return Array.from(JSON.parse(localStorage.getItem(CUSTOM_LAYERS_LS_KEY)));
        } else {
            return [];
        }
    }

    static getObjectsToDisplayInList(
        list: Array<ObjectToDisplay>,
        layerObjectsToDisplay: Array<ObjectToDisplay>,
    ): Array<ObjectToDisplay> {
        const objectsToDisplayTvIds = layerObjectsToDisplay
            .filter(obj => obj.type === MapObjectType.TV)
            .map(obj => MapLayerUtils.EXTENDED_MONITORED_TV_KEY_PROVIDER.getKey(obj.object));
        const objectsToDisplayMeasureIds = layerObjectsToDisplay
            .filter(obj => obj.type === MapObjectType.MEASURE)
            .map(obj => MapLayerUtils.MONITORED_MEASURE_KEY_PROVIDER.getKey(obj.object));
        const objectsToDisplayHotspotTvs = layerObjectsToDisplay
            .filter(obj => obj.type === MapObjectType.HOTSPOT)
            .map(obj => MapLayerUtils.HOTSPOT_TV_ID_PROVIDER.getKey(obj.object));
        return list.filter(inList => {
            if (inList.type === MapObjectType.TV)
                return objectsToDisplayTvIds.includes(
                    MapLayerUtils.EXTENDED_MONITORED_TV_KEY_PROVIDER.getKey(inList.object),
                );
            else if (inList.type === MapObjectType.MEASURE)
                return objectsToDisplayMeasureIds.includes(
                    MapLayerUtils.MONITORED_MEASURE_KEY_PROVIDER.getKey(inList.object),
                );
            else if (inList.type === MapObjectType.HOTSPOT)
                return objectsToDisplayHotspotTvs.includes(MapLayerUtils.HOTSPOT_TV_ID_PROVIDER.getKey(inList.object));
            else return false;
        });
    }

    static containsTv(customLayer: MapLayer, tvId: string): boolean {
        return (
            customLayer?.objectsToDisplay &&
            customLayer.objectsToDisplay.filter(
                obj =>
                    obj.type === MapObjectType.TV &&
                    MapLayerUtils.EXTENDED_MONITORED_TV_TV_ID_PROVIDER.getKey(obj.object) === tvId,
            )?.length > 0
        );
    }

    static containsMeasure(customLayer: MapLayer, measureId: string): boolean {
        return (
            customLayer?.objectsToDisplay &&
            customLayer.objectsToDisplay.some(
                obj =>
                    obj.type === MapObjectType.MEASURE &&
                    MapLayerUtils.MONITORED_MEASURE_KEY_PROVIDER.getKey(obj.object) === measureId,
            )
        );
    }
}

export class WeatherLayerUtils {
    static GROUP_LAYER = 'Environment';
    static CUSTOM_PROPERTY_LEGEND = 'Legend';
    static CUSTOM_PROPERTY_MIN_LEVEL = 'Convection Min Level';
    static CUSTOM_PROPERTY_SHOW_TVS = 'Show TFVs'; //only when meteo source is DWD
    static CUSTOM_PROPERTY_HIDE_CONVECTIONS = 'Hide convection without TV impact';

    static findTrafficVolumeMeteoForecast(
        convectionData: NetworkMapConvection,
        validityPeriod: TimePeriod,
        tvName: string,
    ): TrafficVolumeMeteoForecast {
        if (validityPeriod && tvName) {
            const mapTvConvections = this.mapConvection(
                convectionData.networkForecast,
                validityPeriod,
                convectionData.airspaceDefinition,
            );
            return mapTvConvections?.find(
                value =>
                    Utils.isEquals(value.convection.validityPeriod, validityPeriod) && value.convection.id === tvName,
            )?.convection;
        }
        return null;
    }

    static findPolygonMeteoForecast(convectionData: NetworkMapConvection, id: string): PolygonMeteoForecast {
        if (convectionData?.networkForecast?.polygonMeteoForecasts && id) {
            return convectionData.networkForecast.polygonMeteoForecasts.find(pmf => pmf.id === id);
        }
        return null;
    }

    static mapConvection = memoizeOne(
        (
            convectionData: NetworkMeteoForecast,
            predictionTime: Date | TimePeriod,
            airspaceDef: { [key: string]: Airspace },
        ): Array<MapConvection> => {
            if (convectionData && TimePeriodUtils.intersects(convectionData.forecastValidity, predictionTime)) {
                return Object.keys(convectionData.forecastPerTvName).map(tvName => {
                    return {
                        convection: convectionData.forecastPerTvName[tvName],
                        tvAirspaceMap: airspaceDef[tvName],
                    } as MapConvection;
                });
            }
            console.warn('mapConvection return nothing, prediction time:', predictionTime, convectionData);
            return undefined;
        },
    );
}

export class EnvironmentLayerUtils {
    static ENVIRONMENT_LAYER = 'Environment';
    static MILITARY_ZONES_SEQUENCE_PROP = 'militaryZonesSequenceNumber';
    static MILITARY_ZONES_SEQUENCE_LAST_CHECK_PROP = 'militaryZonesSequenceLastCheck';
    static ACC_ECAC_ONLY = 'ECAC Only';
    static REGULATED_LAYER_MIN_DELAY = 'Min Delay';
    static REGULATED_SECTORS = 'Show sectors';
    static REGULATION_OUTLOOK = 'Show regulation outlook';
    static REGULATION_OUTLOOK_MIN_PREDICTION = 'Min prediction';
    static HOTSPOTS = 'Show hotspots';
}

export interface MeasureMapTrafficVolume extends MapTrafficVolume {
    measureId: string;
}

export enum RegulatedLayerDelayLevelEnum {
    ANY,
    FROM_15,
    FROM_30,
    FROM_45,
}

export enum RegulationOutlookPredictionEnum {
    ALL,
    PRED_30,
    PRED_50,
    PRED_80,
}

export class NatLayerUtils {
    static NAT_LAYER_LAST_UPDATE_PARAMETER: string = 'lastServerUpdate';
    static NAT_SERVER_POLLING_IN_MINUTES: number = 5;
    static isTimeToUpdate(layer: MapLayer): boolean {
        return (
            Utils.isExists(layer.parameters) &&
            Utils.isExists(layer.parameters[this.NAT_LAYER_LAST_UPDATE_PARAMETER]) &&
            layer.parameters[this.NAT_LAYER_LAST_UPDATE_PARAMETER] instanceof Date &&
            TimeUtils.diffInMinutes(new Date(), layer.parameters[this.NAT_LAYER_LAST_UPDATE_PARAMETER] as Date) >
                this.NAT_SERVER_POLLING_IN_MINUTES
        );
    }
}

export class TvImpactLayerUtils {
    static CUSTOM_PROPERTY_SHOW_BEFORE = 'ShowBefore';
    static CUSTOM_PROPERTY_COUNT_TYPE = 'CountType';

    static isSelectionLoadFilter = (obj: ObjectToDisplay, filter: MapLayerFilter): boolean => {
        return obj.type === MapObjectType.IMPACTED_TV && (obj.object as TvImpact)?.type === TvImpactTypeEnum.SELECTION;
    };
    static isNiaImpactedLoadFilter = (obj: ObjectToDisplay, filter: MapLayerFilter): boolean => {
        return (
            obj.type === MapObjectType.IMPACTED_TV && (obj.object as TvImpact)?.type === TvImpactTypeEnum.NIA_IMPACTED
        );
    };
    static isNiaImpactedExtraLoadFilter = (obj: ObjectToDisplay, filter: MapLayerFilter): boolean => {
        return (
            obj.type === MapObjectType.IMPACTED_TV &&
            (obj.object as TvImpact)?.type === TvImpactTypeEnum.NIA_IMPACTED_EXTRA
        );
    };

    static generateSubject(tvId: string, impactType: TvImpactTypeEnum, solutionName: string) {
        return Utils.isExists(solutionName) ? `${tvId}||${impactType}||${solutionName}` : `${tvId}||${impactType}`;
    }

    static parseSubject(subject: string): { tvId: string; impactType: TvImpactTypeEnum; solutionName?: string } {
        const split = subject.split('||');
        return split.length === 3
            ? {
                  tvId: split[0],
                  impactType: split[1] as TvImpactTypeEnum,
                  solutionName: split[2],
              }
            : {
                  tvId: split[0],
                  impactType: split[1] as TvImpactTypeEnum,
              };
    }
}

export class MeasureImpactLayerUtils {
    static generateSubject(regulId: string, solutionName: string) {
        return Utils.isExists(solutionName) ? `${regulId}||${solutionName}` : `${regulId}`;
    }

    static parseSubject(subject: string): { regulationId: string; solutionName?: string } {
        const split = subject.split('||');
        return split.length === 2
            ? {
                  regulationId: split[0],
                  solutionName: split[1],
              }
            : {
                  regulationId: split[0],
              };
    }
}

export interface SolutionBased {
    solutionName: string;
}
export interface SolutionTvImpact extends TvImpact, SolutionBased {}

export interface SolutionMeasureImpact extends NiaMeasureImpact, SolutionBased {}
